// LearningPage — 学習機構 dashboard (= schema-v33 / v37 / v38 / v39, 2026-05-09)
//
// 2-tier TabBar 構造:
//   Level 1: 銀行取引 / 請求書 / 業者別 / 全体サマリ
//     bank → BankLearningTab
//     invoice → InvoiceLearningTab (5 sub-tab: arica/cadence/curated/dedup/outcome)
//     partner → PartnerStatusTab (= Team N, 2026-05-11: 業者別 学習状況 dashboard)
//     all → AllLearningSummary
//
// v39 (Codex F8): URL hash で tab state 永続化 (= reload で位置維持)
//   hash format: #learning              → top=bank
//                #learning/bank          → top=bank
//                #learning/invoice       → top=invoice (sub default)
//                #learning/invoice/arica → top=invoice + sub=arica
//                #learning/partner       → top=partner (Team N)
//                #learning/all           → top=all

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  PageTitle,
  Note,
  Breadcrumbs,
  TabBar,
  CardBase,
  ProgressBar,
  SubSectionTitle,
  MessageBlock,
  InlineSpinner,
  Button,
} from '@freee_jp/vibes';
import { MdShield, MdRefresh, MdReportProblem } from 'react-icons/md';
import { BankLearningTab } from '../components/learning/BankLearningTab';
import { InvoiceLearningTab } from '../components/learning/InvoiceLearningTab';
import { AllLearningSummary } from '../components/learning/AllLearningSummary';
import { PartnerStatusTab } from '../components/learning/PartnerStatusTab';
import { MultiTimingTab } from '../components/learning/MultiTimingTab';
import {
  fetchProcessingFlagsCoverage,
  ProcessingFlagsCoverage,
} from '../api/client';

const TOP_TABS = ['bank', 'invoice', 'partner', 'multi_timing', 'all'] as const;
type TopTabKey = (typeof TOP_TABS)[number];

const INVOICE_SUB_TABS = ['arica', 'cadence', 'curated', 'dedup', 'outcome'] as const;
type InvoiceSubTabKey = (typeof INVOICE_SUB_TABS)[number];

function parseHash(): { top: TopTabKey; sub?: InvoiceSubTabKey } {
  // Strip leading "#" and "learning/" prefix
  const raw = window.location.hash.replace(/^#/, '');
  const parts = raw.split('/').filter(Boolean);
  // Expected: ["learning"] or ["learning", "<top>"] or ["learning", "<top>", "<sub>"]
  // Tolerate also direct usage (without "learning" prefix) for safety
  let topRaw: string | undefined;
  let subRaw: string | undefined;
  if (parts[0] === 'learning') {
    topRaw = parts[1];
    subRaw = parts[2];
  } else {
    topRaw = parts[0];
    subRaw = parts[1];
  }
  const top = (TOP_TABS as readonly string[]).includes(topRaw || '')
    ? (topRaw as TopTabKey)
    : 'bank';
  const sub = (INVOICE_SUB_TABS as readonly string[]).includes(subRaw || '')
    ? (subRaw as InvoiceSubTabKey)
    : undefined;
  return { top, sub };
}

function buildHash(top: TopTabKey, sub?: InvoiceSubTabKey): string {
  if (top === 'invoice' && sub) return `#learning/${top}/${sub}`;
  return `#learning/${top}`;
}

// Phase 2 (2026-05-19): processing_flags 透明化 coverage panel
const ProcessingFlagsCoveragePanel: React.FC = () => {
  const [data, setData] = useState<ProcessingFlagsCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchProcessingFlagsCoverage();
      setData(r);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

  return (
    <CardBase paddingSize="small" mb={0.5}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <SubSectionTitle headlineLevel={3}>
          <MdShield style={{ verticalAlign: 'middle', marginRight: 4, color: '#3949ab' }} />
          processing_flags 透明化 coverage (= schema-v55)
        </SubSectionTitle>
        <Button small appearance="tertiary" onClick={load} disabled={loading}>
          <MdRefresh /> 再取得
        </Button>
      </div>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}
      {loading && !data && (
        <div><InlineSpinner isLoading /> <Note>読込中…</Note></div>
      )}
      {data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 8 }}>
            <div>
              <Note>active 件数</Note>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{data.total_active.toLocaleString()}</div>
              <Note>
                proposed {data.total_proposed} / approved {data.total_approved} /
                registered {data.total_registered} / rejected {data.total_rejected}
              </Note>
            </div>
            <div>
              <Note>過去 24h イベント</Note>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                {data.last_24h_event_count.toLocaleString()}
              </div>
              <Note>(processing_flag_events)</Note>
            </div>
            <div>
              <Note>gate_blocked (累計)</Note>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: data.gate_blocked_count > 0 ? '#a8261a' : '#1e6a36',
              }}>
                {data.gate_blocked_count.toLocaleString()}
              </div>
              <Note>
                <MdReportProblem style={{ verticalAlign: 'middle' }} /> 24h: {data.gate_blocked_24h_count}
              </Note>
            </div>
            <div>
              <Note>生成日時</Note>
              <div style={{ fontSize: '0.75rem', color: '#6b6560' }}>
                {String(data.generated_at).slice(0, 19).replace('T', ' ')}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ minWidth: 220, fontSize: '0.8125rem' }}>past_details_consultation</span>
              <div style={{ flex: 1 }}>
                <ProgressBar value={data.past_details_pct * 100} maxValue={100} />
              </div>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: '0.75rem' }}>
                {pct(data.past_details_pct)} ({data.past_details_done_count}/{data.total_active})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ minWidth: 220, fontSize: '0.8125rem' }}>canonical_provenance</span>
              <div style={{ flex: 1 }}>
                <ProgressBar value={data.canonical_pct * 100} maxValue={100} />
              </div>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: '0.75rem' }}>
                {pct(data.canonical_pct)} ({data.canonical_done_count}/{data.total_active})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ minWidth: 220, fontSize: '0.8125rem' }}>amount_3way_validation</span>
              <div style={{ flex: 1 }}>
                <ProgressBar value={data.amount_3way_pct * 100} maxValue={100} />
              </div>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: '0.75rem' }}>
                {pct(data.amount_3way_pct)} ({data.amount_3way_done_count}/{data.total_active})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ minWidth: 220, fontSize: '0.8125rem' }}>account_name_provenance</span>
              <div style={{ flex: 1 }}>
                <ProgressBar value={data.account_name_pct * 100} maxValue={100} />
              </div>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: '0.75rem' }}>
                {pct(data.account_name_pct)} ({data.account_name_provenance_done_count}/{data.total_active})
              </span>
            </div>
          </div>
        </div>
      )}
    </CardBase>
  );
};

export const LearningPage: React.FC = () => {
  const [topTab, setTopTab] = useState<TopTabKey>(() => parseHash().top);
  const [invoiceSubTab, setInvoiceSubTab] = useState<InvoiceSubTabKey>(
    () => parseHash().sub ?? 'arica',
  );

  // Sync from hash on mount + listen to hashchange (= back/forward + manual edit)
  useEffect(() => {
    const onHash = () => {
      const { top, sub } = parseHash();
      setTopTab(top);
      if (sub) setInvoiceSubTab(sub);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Push hash whenever local state changes
  useEffect(() => {
    const expected = buildHash(topTab, topTab === 'invoice' ? invoiceSubTab : undefined);
    if (window.location.hash !== expected) {
      // replaceState avoids polluting back-button history with every tab click
      window.history.replaceState(null, '', expected);
    }
  }, [topTab, invoiceSubTab]);

  const onChangeTopTab = useCallback((i: number) => {
    setTopTab(TOP_TABS[i]);
  }, []);
  const onChangeInvoiceSubTab = useCallback((sub: InvoiceSubTabKey) => {
    setInvoiceSubTab(sub);
  }, []);

  const topIndex = Math.max(0, TOP_TABS.indexOf(topTab));

  return (
    <Container>
      <Breadcrumbs links={[{ url: '#', title: 'FREEEMAN' }, { url: '#learning', title: '学習機構' }]} />
      <PageTitle>学習機構</PageTitle>
      <Note>銀行取引 + 請求書 ベースの 機械学習 dashboard (= bank_pattern_index / partner_curated_patterns / invoice_outcome_log)</Note>

      {/* Phase 2 (2026-05-19): processing_flags 透明化 coverage */}
      <ProcessingFlagsCoveragePanel />

      <CardBase paddingSize="small" mb={0.5}>
        <TabBar
          tabs={['銀行取引', '請求書', '業者別', '多 timing hook', '全体サマリ']}
          currentTabIndex={topIndex}
          onClickTab={onChangeTopTab}
        />
      </CardBase>

      {topTab === 'bank' && <BankLearningTab />}
      {topTab === 'invoice' && (
        <InvoiceLearningTab subTab={invoiceSubTab} onChangeSubTab={onChangeInvoiceSubTab} />
      )}
      {topTab === 'partner' && <PartnerStatusTab />}
      {topTab === 'multi_timing' && <MultiTimingTab />}
      {topTab === 'all' && <AllLearningSummary />}
    </Container>
  );
};
